import { useState } from "react";
import { useTranslation } from "@localization/useTranslation";
import { RelativeDateDirection, RelativeDateUnit } from "../../../../annualWheel/RelativeDate.types.ts";
import { DateFieldValue, RollingPeriodValue } from "./DateFieldSubMenu.tsx";

export const maxRollingPeriodValue = 999;

export interface RollingPeriodSectionProps {
    value: DateFieldValue;
    hideFutureDates: boolean | undefined;
    onChange: (value: DateFieldValue) => void;
}

interface RollingPeriodDraft {
    value: string;
    unit: RelativeDateUnit;
    direction: RelativeDateDirection;
}

function getRollingPeriodKey(rollingPeriod: RollingPeriodValue | undefined) {
    return rollingPeriod ? `${rollingPeriod.direction}-${rollingPeriod.value}-${rollingPeriod.unit}` : "";
}

export function useRollingPeriodSection(props: RollingPeriodSectionProps) {
    const { translateString } = useTranslation();
    const committed = props.value.rollingPeriod;
    const defaultDirection = props.hideFutureDates ? RelativeDateDirection.Before : RelativeDateDirection.After;

    const [draft, setDraft] = useState<RollingPeriodDraft>(() => toDraft(committed, defaultDirection));
    const [committedKey, setCommittedKey] = useState(getRollingPeriodKey(committed));

    // The value can change from outside: a preset or From/To replaces the period, and a saved view loads one.
    // Adjusting the draft during render, rather than in an effect, keeps it from showing the old period for a frame.
    // The key is compared rather than the object, because the parent parses a fresh object on every render.
    const currentKey = getRollingPeriodKey(committed);
    if (currentKey !== committedKey) {
        setCommittedKey(currentKey);
        setDraft(committed ? toDraft(committed, defaultDirection) : { ...draft, value: "" });
    }

    // Only a complete period reaches the filter, so a half-filled one never ends up in a saved view. The number
    // field clamps to 1-999 itself, which leaves an empty number as the only incomplete state.
    function commit(nextDraft: RollingPeriodDraft) {
        setDraft(nextDraft);

        if (nextDraft.value) {
            const rollingPeriod = { value: Number(nextDraft.value), unit: nextDraft.unit, direction: nextDraft.direction };
            props.onChange({ rollingPeriod, from: undefined, to: undefined, dateFieldEnum: undefined });
        } else if (committed) {
            props.onChange({ rollingPeriod: undefined, from: undefined, to: undefined, dateFieldEnum: undefined });
        }
    }

    const directionOptions = [
        { id: RelativeDateDirection.After.toString(), name: translateString("nextPeriod") },
        { id: RelativeDateDirection.Before.toString(), name: translateString("lastPeriod") },
    ];

    const unitOptions = [
        { id: RelativeDateUnit.Days.toString(), name: translateString("days") },
        { id: RelativeDateUnit.Weeks.toString(), name: translateString("weeks") },
        { id: RelativeDateUnit.Months.toString(), name: translateString("months") },
        { id: RelativeDateUnit.Years.toString(), name: translateString("years") },
    ];

    return {
        draft,
        directionOptions,
        unitOptions,
        onValueChange: (value: string) => commit({ ...draft, value }),
        onDirectionChange: (direction: string | null) => commit({ ...draft, direction: Number(direction) }),
        onUnitChange: (unit: string | null) => commit({ ...draft, unit: Number(unit) }),
    };
}

function toDraft(rollingPeriod: RollingPeriodValue | undefined, defaultDirection: RelativeDateDirection): RollingPeriodDraft {
    if (rollingPeriod) {
        return { value: rollingPeriod.value.toString(), unit: rollingPeriod.unit, direction: rollingPeriod.direction };
    }

    return { value: "", unit: RelativeDateUnit.Months, direction: defaultDirection };
}
